import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from "react";
import { listen, platform } from "./tauri-web";
import { openFileDialog, saveFileDialog } from "./tauri-web";
import { writeTextFile } from "./tauri-web";
import { supabase } from "./supabase";
import { AiConsent, AI_CONSENT_KEY, signInIos, signInMac } from "./ios-native-kit";
import { AccountMenu } from "./AccountMenu";
import { AddFundsMenu } from "./AddFundsMenu";
import { PhotoViewer } from "./PhotoViewer";
import {
  store, DEFAULT_CATEGORIES, DEFAULT_COLORS, normalizeLower, titleCase,
  priceToCents, centsToInput, formatCents, costPerWearCents, dormancyCopy, fmtDate,
  todayLocal, collectionIds, type Garment, type ClosetSignals, type FactTone, type Collection,
} from "./store";
import { closetRecap, askCloset, suggestOutfits, identifyGarment, getWeather, openWebLogin, BrainError, type BrainErrorCode, type OutfitResult, type WeatherNow } from "./brain";
import { OrioraBadge } from "./OrioraBadge";
import { buildExport } from "./export";
import { BackupMenu } from "./BackupMenu";
import Chat from "./chat/Chat";
import {
  Sparkles, Shirt, Trash2, X, Lock, WifiOff, RefreshCw, Check, Pencil, Plus, Search,
  Coins, Layers, CalendarClock, Wallet, MessageCircleQuestion, Copy, Wand2, Mic,
} from "lucide-react";

import glyphTops from "./assets/glyph-tops.svg";
import glyphBottoms from "./assets/glyph-bottoms.svg";
import glyphDresses from "./assets/glyph-dresses.svg";
import glyphOuterwear from "./assets/glyph-outerwear.svg";
import glyphShoes from "./assets/glyph-shoes.svg";
import glyphKnitwear from "./assets/glyph-knitwear.svg";
import glyphActivewear from "./assets/glyph-activewear.svg";
import glyphBags from "./assets/glyph-bags.svg";
import glyphAccessories from "./assets/glyph-accessories.svg";
import glyphJewellery from "./assets/glyph-jewellery.svg";
import momentEmptyCloset from "./assets/moment-empty-closet.svg";
import momentFirstWear from "./assets/moment-first-wear.svg";

const LIST_CAP = 50000;

// Category → placeholder glyph (assets ship 1:1 with DEFAULT_CATEGORIES; unknown → tops).
const CATEGORY_GLYPHS: Record<string, string> = {
  tops: glyphTops, bottoms: glyphBottoms, dresses: glyphDresses, outerwear: glyphOuterwear,
  shoes: glyphShoes, knitwear: glyphKnitwear, activewear: glyphActivewear, bags: glyphBags,
  accessories: glyphAccessories, jewellery: glyphJewellery,
};
const glyphFor = (category: string) => CATEGORY_GLYPHS[(category || "").toLowerCase()] ?? glyphTops;

// Garment colour name → faint tile tint for the photo-less cell (--swatch).
const COLOR_TINTS: Record<string, string> = {
  black: "rgba(28,28,28,0.10)", white: "rgba(255,255,255,0.35)", grey: "rgba(154,154,154,0.12)",
  gray: "rgba(154,154,154,0.12)", navy: "rgba(31,45,84,0.14)", blue: "rgba(31,45,84,0.12)",
  camel: "rgba(185,138,82,0.12)", brown: "rgba(140,100,60,0.12)", beige: "rgba(216,199,168,0.12)",
  cream: "rgba(230,220,195,0.18)", green: "rgba(74,138,92,0.12)", sage: "rgba(120,150,120,0.12)",
  olive: "rgba(110,120,70,0.12)", red: "rgba(140,47,57,0.12)", burgundy: "rgba(140,47,57,0.14)",
  pink: "rgba(200,120,150,0.12)", purple: "rgba(120,90,160,0.12)", yellow: "rgba(200,170,60,0.12)",
  orange: "rgba(200,120,60,0.12)", indigo: "rgba(50,60,120,0.14)", emerald: "rgba(20,110,80,0.12)",
};
const tintFor = (color: string) => COLOR_TINTS[(color || "").toLowerCase()] ?? "transparent";

type ClosetTab = "closet" | "truth" | "style" | "you";

// SCREENSHOT=0 — sim-only auth/consent bypass for identity screenshots. MUST stay 0 in committed/shipped code.
const SCREENSHOT = 0;

/**
 * 'know what you actually wear' — the hanger everything in a wardrobe hangs from.
 * The last of the shared lucide `Sparkles` launcher glyphs — 20 of the fleet's 26 chat launchers
 * rendered it in common, which is the shared-fingerprint half of Guideline 4.3(a). This app's
 * OUTLINE was already its own, so only the glyph needed to move.
 */
function HangerMark({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M12 4.2a1.9 1.9 0 0 1 1.9 1.9c0 1.1-.9 1.5-1.9 1.9v1.4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <path d="m12 9.4 7.6 6.1a1 1 0 0 1-.6 1.8H5a1 1 0 0 1-.6-1.8z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    </svg>
  );
}

export default function App() {
  const [mounted, setMounted] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [email, setEmail] = useState<string | null>(null);
  // AI-consent gate (APP-READY-STANDARD §2/§3, Apple Guideline 5.1.1(i)). Shown ONCE on first
  // launch before anything else — names the data sent + the AI vendors. Persisted in localStorage.
  const [consented, setConsented] = useState<boolean>(() => {
    try { return localStorage.getItem(AI_CONSENT_KEY) === "1"; } catch { return false; }
  });

  // A deleted/revoked account keeps a locally-valid (unexpired) JWT, so getSession() still
  // returns it and the app looks signed-in. getUser() asks the SERVER — on a real auth
  // rejection (401/403) we purge the dead session and drop to login. Network errors (no
  // status) are ignored so an offline-but-valid user is never signed out. (mirrors expense_audit_ai)
  const revalidate = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const { error } = await supabase.auth.getUser();
      const status = (error as { status?: number } | null)?.status;
      if (error && (status === 401 || status === 403)) {
        try { await supabase.auth.signOut({ scope: "local" }); } catch { /* ignore */ }
        setEmail(null); setAuthed(false);
      }
    } catch { /* network/unknown → keep session (offline-tolerant) */ }
  }, []);

  useEffect(() => {
    // onAuthStateChange fires INITIAL_SESSION after async filesystem storage loads —
    // avoids the cold-launch race where getSession() returned null before storage was ready.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      setAuthed(session !== null);
      setEmail(session?.user?.email ?? null);
      setMounted(true);
      if (_e === 'INITIAL_SESSION' && session) void revalidate();
    });
    const timeout = setTimeout(() => setMounted(true), 3000);
    return () => { clearTimeout(timeout); subscription.unsubscribe(); };
  }, [revalidate]);

  // Re-validate whenever the app returns to the foreground (catches "deleted my account,
  // came back, still signed in").
  useEffect(() => {
    const recheck = () => { if (document.visibilityState === "visible") void revalidate(); };
    document.addEventListener("visibilitychange", recheck);
    window.addEventListener("focus", recheck);
    return () => { document.removeEventListener("visibilitychange", recheck); window.removeEventListener("focus", recheck); };
  }, [revalidate]);

  // Deep-link auth: web login returns oriora-closet://auth#access_token=…&refresh_token=…
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen<string>("auth:deep-link", async (e) => {
      try {
        const u = new URL(e.payload);
        const frag = new URLSearchParams(u.hash.replace(/^#/, ""));
        const access_token = frag.get("access_token");
        const refresh_token = frag.get("refresh_token");
        if (access_token && refresh_token) await supabase.auth.setSession({ access_token, refresh_token });
      } catch (err) {
        console.error("deep-link auth failed", err);
      }
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, []);

  // Account-deletion handback: the in-app delete window finished + navigated to
  // oriora-closet://deleted. Sign the app out so it drops to the logged-out screen.
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen("account:deleted", async () => {
      try { await supabase.auth.signOut({ scope: "local" }); } catch { /* ignore */ }
      setEmail(null); setAuthed(false);
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, []);

  // First-launch AI-consent gate — before login + before any AI feature is reachable.
  if (!consented && !SCREENSHOT) {
    return <AiConsent brand="Oriora Closet"
      onAccept={() => { try { localStorage.setItem(AI_CONSENT_KEY, "1"); } catch { /* still consented this session */ } setConsented(true); }} />;
  }
  if (!mounted && !SCREENSHOT) return <BootSplash />;
  if (!authed && !SCREENSHOT) return <Login />;
  return <Home email={SCREENSHOT ? "you@oriora.app" : email} onSignedOut={() => { setEmail(null); setAuthed(false); }} />;
}

function BootSplash() {
  return (
    <div style={{ height: "100dvh", display: "grid", placeItems: "center", background: "var(--bg)" }}>
      <div style={{ display: "grid", placeItems: "center", gap: "0.9rem" }}>
        <div className="empty-art" style={{ width: 64, height: 64, borderRadius: 18 }}><Shirt size={30} /></div>
        <div className="skeleton-line" style={{ width: 120, height: 10 }} />
      </div>
    </div>
  );
}

function Login() {
  const [busy, setBusy] = useState(false);
  return (
    <main style={{ height: "100dvh", display: "flex", flexDirection: "column", overflowY: "auto", WebkitOverflowScrolling: "touch" as any, boxSizing: "border-box", padding: "max(1.5rem, env(safe-area-inset-top)) 1.5rem max(1.5rem, env(safe-area-inset-bottom))" }}>
      <div className="card fade-up" style={{ width: "100%", maxWidth: 380, padding: "2.25rem 2rem", textAlign: "center", borderRadius: "var(--radius-lg)" }}>
        <div className="empty-art" style={{ marginBottom: "1rem" }}><Shirt size={34} /></div>
        <div className="eyebrow" style={{ justifyContent: "center" }}>Oriora Closet</div>
        <h1 className="serif" style={{ fontSize: "1.55rem", margin: "0.5rem 0 0.5rem", letterSpacing: "-0.02em" }}>Know what you actually wear.</h1>
        <p className="muted" style={{ fontSize: "var(--fs-sm)", lineHeight: 1.6, marginBottom: "1.5rem" }}>
          Log what you own and each time you wear it. It's kept on this device unless you back it up
          to your own storage. Oriora reads the cost-per-wear back to you when you ask, through your
          own account, and keeps nothing after the answer.
        </p>
        <button className="btn btn-primary btn-pill" style={{ width: "100%", minHeight: 48 }} disabled={busy}
          onClick={async () => {
            setBusy(true);
            // iOS → in-app ASWebAuthenticationSession (Apple Guideline 4, #61); desktop → in-app login window.
            try {
              const p = platform();
              if (p === "ios") await signInIos("oriora-closet");
              else if (p === "macos") await signInMac("oriora-closet");
              // Web: same-origin login. Go to the site login and return to this app's path; the
              // Supabase session is shared on the orioralabs.com origin, so we come back signed in.
              else if (p === "web") window.location.href = "/login?next=/oriora-closet/";
              else await openWebLogin();
            }
            catch { /* user dismissed the sheet or it failed — reset and let them retry */ }
            finally { setBusy(false); }
          }}>
          {busy ? "Signing in…" : "Sign in with Oriora"}
        </button>
        <p className="muted" style={{ fontSize: "var(--fs-xxs)", marginTop: "1rem", lineHeight: 1.5 }}>
          Opens Oriora to sign in, then returns here.
        </p>
      </div>
    </main>
  );
}

function Home({ email, onSignedOut }: { email: string | null; onSignedOut: () => void }) {
  const [garments, setGarments] = useState<Garment[]>([]);
  const [looksKey, setLooksKey] = useState(0); // bump to re-fetch saved looks after a save/wear
  const [loaded, setLoaded] = useState(false);
  const [editing, setEditing] = useState<Garment | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<Garment | null>(null);
  const [justWore, setJustWore] = useState<string | null>(null);
  const [offline, setOffline] = useState(typeof navigator !== "undefined" && navigator.onLine === false);

  useEffect(() => {
    const goOnline = () => setOffline(false);
    const goOffline = () => setOffline(true);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => { window.removeEventListener("online", goOnline); window.removeEventListener("offline", goOffline); };
  }, []);

  const recapRef = useRef<HTMLDivElement>(null);
  const exportRef = useRef<(() => void) | null>(null);

  const [chatOpen, setChatOpen] = useState(false);
  // R1–R4 (2026-08-05 redesign): the app is four tabbed surfaces on the rail.
  const [tab, setTab] = useState<ClosetTab>("closet");
  const [openGarment, setOpenGarment] = useState<Garment | null>(null);
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState<string>("all");
  const [firstWearMoment, setFirstWearMoment] = useState(false);

  // The home never read signals — only InsightsPanel did, further down the page. But signals is
  // where cost-per-wear and dormancy live, which is what the rack ranks by.
  const [homeSignals, setHomeSignals] = useState<ClosetSignals | null>(null);

  const reload = useCallback(async () => {
    setGarments(await store.listGarments(LIST_CAP));
    try { setHomeSignals(await store.computeSignals(todayLocal())); } catch { setHomeSignals(null); }
    setLoaded(true);
  }, []);
  useEffect(() => { void reload(); }, [reload]);

  const totalWears = useMemo(() => garments.reduce((a, g) => a + g.wear_count, 0), [garments]);
  const hasData = garments.length > 0;

  const doExport = useCallback(() => {
    if (garments.length === 0) return;
    const { csv, filename } = buildExport(garments);
    void (async () => { const p = await saveFileDialog({ defaultPath: filename }); if (p) await writeTextFile(p, csv); })();
  }, [garments]);
  exportRef.current = doExport;

  // Native-menu actions (Actions menu in the macOS menu bar): Add / Recap / Export.
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen<string>("menu:action", (e) => {
      switch (e.payload) {
        case "add": setEditing(null); setShowForm(true); break;
        case "recap":
          recapRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
          recapRef.current?.querySelector<HTMLButtonElement>("button")?.click();
          break;
        case "export": exportRef.current?.(); break;
      }
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, []);

  function startForm(g?: Garment) { setEditing(g ?? null); setShowForm(true); }

  async function doDelete(id: string) {
    setConfirmDelete(null);
    await store.deleteGarment(id);
    await reload();
  }

  // Body is the scroll container: tab switches and the object page must land at the top.
  useEffect(() => { try { document.body.scrollTo(0, 0); window.scrollTo(0, 0); } catch { /* ignore */ } }, [tab, openGarment?.id]);

  async function doLogWear(g: Garment) {
    try {
      const firstEver = garments.reduce((a, x) => a + x.wear_count, 0) === 0;
      await store.logWear(g.id, todayLocal());
      setJustWore(g.id);
      setTimeout(() => setJustWore((cur) => (cur === g.id ? null : cur)), 1400);
      if (firstEver) { setFirstWearMoment(true); setTimeout(() => setFirstWearMoment(false), 2000); }
      await reload();
      // keep an open object page showing the fresh numbers
      setOpenGarment((cur) => (cur && cur.id === g.id ? { ...cur, wear_count: cur.wear_count + 1, last_worn_on: todayLocal() } as Garment : cur));
    } catch (e) { console.error("log wear failed", e); }
  }

  if (!loaded) return <BootSplash />;

  // wardrobe filtering (on-device, instant)
  const q = search.trim().toLowerCase();
  const filtered = garments.filter((g) => {
    if (catFilter !== "all" && (g.category || "").toLowerCase() !== catFilter) return false;
    if (!q) return true;
    return `${g.item} ${g.brand} ${g.category} ${g.color}`.toLowerCase().includes(q);
  });
  const catCounts = new Map<string, number>();
  for (const g of garments) {
    const c = (g.category || "other").toLowerCase();
    catCounts.set(c, (catCounts.get(c) ?? 0) + 1);
  }

  const tabs: { key: ClosetTab; label: string; icon: React.ReactNode }[] = [
    { key: "closet", label: "Closet", icon: <Shirt size={17} /> },
    { key: "truth", label: "Truth", icon: <Coins size={17} /> },
    { key: "style", label: "Style", icon: <Wand2 size={17} /> },
    { key: "you", label: "You", icon: <Wallet size={17} /> },
  ];

  // R3 — the garment object page is its own surface.
  if (openGarment) {
    const live = garments.find((g) => g.id === openGarment.id) ?? openGarment;
    return (
      <GarmentPage
        g={live}
        wore={justWore === live.id}
        onBack={() => setOpenGarment(null)}
        onWear={() => doLogWear(live)}
        onEdit={() => startForm(live)}
        onDelete={() => setConfirmDelete(live)}
        form={showForm && (
          <GarmentForm editing={editing} onClose={() => { setShowForm(false); setEditing(null); }} onSaved={() => { setShowForm(false); setEditing(null); void reload(); }} />
        )}
        confirm={confirmDelete && (
          <CenterModal title="Delete this piece?" onClose={() => setConfirmDelete(null)}>
            <p className="muted" style={{ fontSize: "var(--fs-sm)", margin: "0 0 1.25rem", lineHeight: 1.5 }}>
              “{confirmDelete.item}” and its {confirmDelete.wear_count} logged {confirmDelete.wear_count === 1 ? "wear" : "wears"} go with it. That can’t be undone.
            </p>
            <div className="row" style={{ justifyContent: "flex-end", gap: "0.5rem" }}>
              <button className="btn" onClick={() => setConfirmDelete(null)}>Cancel</button>
              <button className="btn btn-primary" style={{ background: "var(--rose)", borderColor: "var(--rose)" }} onClick={() => { setOpenGarment(null); void doDelete(confirmDelete.id); }}>Delete</button>
            </div>
          </CenterModal>
        )}
      />
    );
  }

  return (
    <main className="shell" style={{
      paddingTop: "max(1.1rem, env(safe-area-inset-top))",
      paddingBottom: "calc(84px + env(safe-area-inset-bottom))",
    }}>
      <div className="rail rail-draw" aria-hidden="true" />

      {offline && (
        <div role="status" className="notice notice-warn" style={{ marginBottom: "1rem" }}>
          <WifiOff size={15} style={{ flexShrink: 0, marginTop: 1 }} />
          <span><strong>Offline.</strong> The read is paused; your wardrobe still saves on this device.</span>
        </div>
      )}

      <header className="topbar">
        <div>
          <h1 className="topbar-title">{tab === "closet" ? "Closet" : tab === "truth" ? "The truth" : tab === "style" ? "Style me" : "You"}</h1>
          <div className="topbar-count">
            {garments.length === 0 ? "Nothing logged yet" : `${garments.length} ${garments.length === 1 ? "piece" : "pieces"} · ${totalWears} ${totalWears === 1 ? "wear" : "wears"}`}
          </div>
        </div>
        {tab !== "you" && (
          <div className="row" style={{ gap: "0.4rem", alignItems: "center", flexWrap: "nowrap" }}>
            <BackupMenu
              appName="Closet" fileSlug="closet" appScheme="oriora-closet"
              hasData={garments.length > 0} onRestored={reload}
              onExportCsv={garments.length > 0 ? doExport : undefined}
            />
            <AccountMenu email={email} appSlug="oriora-closet" appName="Oriora Closet" onSignedOut={onSignedOut} />
            <AddFundsMenu />
          </div>
        )}
      </header>

      {/* The landing surface needs its own way in: the floating bubble that used to carry chat here was
          retired 2026-09-21 (it covered the Style primary). Same in-flow launcher the Style surface uses
          — same class, same handler, same gating — rendered once per branch exactly as Style does, so one
          is on screen at a time. On THIS tab it sits ABOVE the wardrobe, directly under the filter chips:
          the Closet surface also carries the fixed "Add" FAB (bottom 64px, 48px tall), and measured at 390
          a foot placement put the row's bottom-right 84×24 underneath it. The empty state has no chips and
          no FAB, so there it keeps Style's foot placement. */}
      {tab === "closet" && (
        garments.length === 0 ? (
          <>
            <EmptyCloset onAdd={() => startForm()} onStarted={reload} />
            {!chatOpen && (
              <button className="ocl-launch" onClick={() => setChatOpen(true)} aria-label="Open chat assistant">
                <HangerMark size={16} /> Or just say it — add, log, find, summarise
              </button>
            )}
          </>
        ) : (
          <>
            <div className="searchfield">
              <Search size={16} />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search your closet" aria-label="Search your closet" />
            </div>
            <div className="catbar">
              <button className={`chip ${catFilter === "all" ? "selected" : ""}`} onClick={() => setCatFilter("all")}>
                All <span className="tnum" style={{ opacity: 0.65 }}>{garments.length}</span>
              </button>
              {DEFAULT_CATEGORIES.filter((c) => (catCounts.get(c) ?? 0) > 0).map((c) => (
                <button key={c} className={`chip ${catFilter === c ? "selected" : ""}`} onClick={() => setCatFilter(catFilter === c ? "all" : c)}>
                  {titleCase(c)} <span className="tnum" style={{ opacity: 0.65 }}>{catCounts.get(c)}</span>
                </button>
              ))}
            </div>
            {!chatOpen && (
              <button className="ocl-launch" onClick={() => setChatOpen(true)} aria-label="Open chat assistant">
                <HangerMark size={16} /> Or just say it — add, log, find, summarise
              </button>
            )}
            {firstWearMoment && (
              <div className="empty-art fade-up" style={{ marginBottom: "0.75rem" }}><img src={momentFirstWear} alt="First wear logged" /></div>
            )}
            {filtered.length === 0 ? (
              <p className="muted" style={{ fontSize: "var(--fs-sm)", padding: "1rem 0" }}>Nothing matches{q ? ` “${search.trim()}”` : ""}.</p>
            ) : (
              <div className="wardrobe">
                {filtered.map((g) => (
                  <GarmentCell key={g.id} g={g} onOpen={() => setOpenGarment(g)} />
                ))}
              </div>
            )}
          </>
        )
      )}

      {tab === "truth" && (
        garments.length === 0 ? <EmptyCloset onAdd={() => { setTab("closet"); startForm(); }} /> : (
          <>
            <TruthLede garments={garments} totalWears={totalWears} signals={homeSignals} />
            <WearRack signals={homeSignals} garments={garments} />
            {hasData && <InsightsPanel garments={garments} />}
            <div ref={recapRef}>
              <ClosetRecapCard garments={garments} />
            </div>
            <AskClosetCard garments={garments} />
          </>
        )
      )}

      {tab === "style" && garments.length === 0 && (
        <>
          <EmptyCloset onAdd={() => { setTab("closet"); startForm(); }} />
          {!chatOpen && (
            <button className="ocl-launch" onClick={() => setChatOpen(true)} aria-label="Open chat assistant">
              <HangerMark size={16} /> Or just say it — add, log, find, summarise
            </button>
          )}
        </>
      )}
      {tab === "style" && garments.length > 0 && (
          <>
            <OutfitCard garments={garments} onSaved={() => setLooksKey((k) => k + 1)} />
            <SavedLooks garments={garments} refreshKey={looksKey} onChanged={() => { setLooksKey((k) => k + 1); void reload(); }} />
            {!chatOpen && (
              <button className="ocl-launch" onClick={() => setChatOpen(true)} aria-label="Open chat assistant">
                <HangerMark size={16} /> Or just say it — add, log, find, summarise
              </button>
            )}
          </>
      )}

      {tab === "you" && (
        <div style={{ display: "grid", gap: "0.5rem" }}>
          <div className="tile" style={{ alignItems: "center" }}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: "var(--fs-md)", fontWeight: 600 }}>Account</div>
              <div className="muted" style={{ fontSize: "var(--fs-xs)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{email ?? "Signed in with Oriora"}</div>
            </div>
            <AccountMenu email={email} appSlug="oriora-closet" appName="Oriora Closet" onSignedOut={onSignedOut} />
            <AddFundsMenu />
          </div>
          <div className="tile" style={{ alignItems: "center" }}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: "var(--fs-md)", fontWeight: 600 }}>Backup &amp; export</div>
              <div className="muted" style={{ fontSize: "var(--fs-xs)" }}>Your own GitHub or Drive, or a local file.</div>
            </div>
            <BackupMenu
              appName="Closet" fileSlug="closet" appScheme="oriora-closet"
              hasData={garments.length > 0} onRestored={reload}
              onExportCsv={garments.length > 0 ? doExport : undefined}
            />
          </div>
          <p className="muted" style={{ fontSize: "var(--fs-xs)", lineHeight: 1.6, margin: "0.4rem 0 0", padding: "0 0.2rem" }}>
            The wardrobe itself — photos, prices, wears — is kept on this device. A backup sends it through Oriora to your own storage, and "Fill details from photo" sends that one photo for that one reading; nothing is retained either way. The account exists so those calls are billed to your own Oriora wallet.
          </p>
        </div>
      )}

      {tab === "closet" && garments.length > 0 && (
        <button className="fab" onClick={() => startForm()} aria-label="Add a piece"><Plus size={17} /> Add</button>
      )}

      <nav className="tabbar" aria-label="Sections">
        {tabs.map((t) => (
          <button key={t.key} className={`tab ${tab === t.key ? "on" : ""}`} onClick={() => { setTab(t.key); setChatOpen(false); }} aria-current={tab === t.key}>
            {t.icon}{t.label}
          </button>
        ))}
      </nav>

      {showForm && (
        <GarmentForm editing={editing} onClose={() => { setShowForm(false); setEditing(null); }} onSaved={() => { setShowForm(false); setEditing(null); void reload(); }} />
      )}

      {confirmDelete && (
        <CenterModal title="Delete this piece?" onClose={() => setConfirmDelete(null)}>
          <p className="muted" style={{ fontSize: "var(--fs-sm)", margin: "0 0 1.25rem", lineHeight: 1.5 }}>
            “{confirmDelete.item}” and its {confirmDelete.wear_count} logged {confirmDelete.wear_count === 1 ? "wear" : "wears"} go with it. That can’t be undone.
          </p>
          <div className="row" style={{ justifyContent: "flex-end", gap: "0.5rem" }}>
            <button className="btn" onClick={() => setConfirmDelete(null)}>Cancel</button>
            <button className="btn btn-primary" style={{ background: "var(--rose)", borderColor: "var(--rose)" }} onClick={() => doDelete(confirmDelete.id)}>Delete</button>
          </div>
        </CenterModal>
      )}

      <aside className={`ocl-sheet ${chatOpen ? "open" : ""}`} aria-hidden={!chatOpen}>
        <div className="ocl-head">
          <span className="row" style={{ gap: "0.35rem" }}><Sparkles size={14} /> Say it plainly — add, log, find, summarise</span>
          <button className="link" onClick={() => setChatOpen(false)}>Close ↓</button>
        </div>
        <Chat onChanged={reload} />
      </aside>
    </main>
  );
}

/* ── R1 · the wardrobe cell — the garment IS the interface (2026-08-05 redesign) ── */
function GarmentCell({ g, onOpen }: { g: Garment; onOpen: () => void }) {
  const cpw = costPerWearCents(g);
  const hasCpw = cpw !== null && g.wear_count > 0;
  return (
    <button className="gcell" onClick={onOpen} aria-label={`${g.item} — open`}>
      {g.photo_ref ? (
        <span className="gcell-mat"><img className="gcell-photo" src={g.photo_ref} alt="" loading="lazy" /></span>
      ) : (
        <span className="gcell-ph" style={{ ["--swatch" as never]: tintFor(g.color) }}>
          <img src={glyphFor(g.category)} alt="" />
        </span>
      )}
      <span className={`gcell-tag ${hasCpw ? "" : "idle"}`}>
        {hasCpw ? <>{formatCents(cpw!)}<span className="unit">/wear</span></> : <>no wears<span className="unit" /></>}
      </span>
      {g.wear_count > 0 && (
        <span className="tally" aria-hidden="true">
          {Array.from({ length: Math.min(g.wear_count, 14) }).map((_, i) => <span key={i} className="tally-tick" />)}
        </span>
      )}
      <span className="gcell-scrim">
        <span className="gcell-name">{g.item}</span>
        <span className="gcell-meta">{[g.brand, dormancyCopy(g)].filter(Boolean).join(" · ")}</span>
      </span>
    </button>
  );
}

/* ── R3 · the garment object page ── */
function GarmentPage({ g, wore, onBack, onWear, onEdit, onDelete, form, confirm }: {
  g: Garment; wore: boolean; onBack: () => void; onWear: () => void; onEdit: () => void; onDelete: () => void;
  form?: React.ReactNode; confirm?: React.ReactNode;
}) {
  const cpw = costPerWearCents(g);
  const hasCpw = cpw !== null && g.wear_count > 0;
  const [viewing, setViewing] = useState(false);
  return (
    <main className="shell obj" style={{ paddingTop: "max(0.6rem, env(safe-area-inset-top))", paddingInline: 0 }}>
      <div style={{ padding: "0 var(--sp-5)" }}>
        <div className="rail rail-draw" aria-hidden="true" />
        <div className="row" style={{ justifyContent: "space-between", marginBottom: "0.6rem" }}>
          <button className="btn-ghost" onClick={onBack}>← Closet</button>
          <div className="row" style={{ gap: "0.2rem", flexWrap: "nowrap" }}>
            <button className="btn-ghost" onClick={onEdit}><Pencil size={15} /> Edit</button>
            <button className="btn-ghost" onClick={onDelete} aria-label="Delete piece"><Trash2 size={15} /></button>
          </div>
        </div>
      </div>
      <div className={`obj-hero${g.photo_ref ? "" : " is-empty"}`}
        onClick={() => (g.photo_ref ? setViewing(true) : onEdit())}
        role="button"
        aria-label={g.photo_ref ? "View photo" : `Add a photo of ${g.item}`}>
        {g.photo_ref ? (
          <img src={g.photo_ref} alt={g.item} />
        ) : (
          <>
            <span className="gcell-ph" style={{ ["--swatch" as never]: tintFor(g.color) }}>
              <img src={glyphFor(g.category)} alt="" />
            </span>
            <span className="obj-hero-add">Add a photo of your {g.category ? titleCase(g.category).toLowerCase().replace(/s$/, "") : "piece"}</span>
          </>
        )}
        {/* The cost-per-wear tag only rides ON a photo. In the empty state it collided with the
            "Add a photo" line AND repeated the ledger below (Wears 0 / Cost per wear —). */}
        {g.photo_ref && (
          <span className={`gcell-tag ${hasCpw ? "" : "idle"} ${wore ? "tag-flash" : ""}`}>
            {hasCpw ? <>{formatCents(cpw!)}<span className="unit">/wear</span></> : <>no wears<span className="unit" /></>}
          </span>
        )}
      </div>
      <div style={{ padding: "0 var(--sp-5)" }}>
        <h1 className="obj-title">{g.item}</h1>
        <div className="obj-sub">{[g.brand, titleCase(g.category || ""), g.color ? titleCase(g.color) : ""].filter(Boolean).join(" · ")}</div>
        <div className="obj-ledger">
          {g.price_cents > 0 && (
            <div className="led-row"><span className="led-k">Paid</span><span className="led-v tnum">{formatCents(g.price_cents)}</span></div>
          )}
          <div className="led-row led-key">
            <span className="led-k">Cost per wear</span>
            <span className={`led-v tnum cpw-roll`}><span key={g.wear_count} className={wore ? "cpw-in" : undefined}>{hasCpw ? formatCents(cpw!) : "—"}</span></span>
          </div>
          <div className="led-row"><span className="led-k">Wears</span><span className="led-v tnum">{g.wear_count}</span></div>
          <div className="led-row"><span className="led-k">Last worn</span><span className="led-v">{g.last_worn_on ? fmtDate(g.last_worn_on) : "Never"}</span></div>
          {(g.purchased_on || g.created_at) && (
            <div className="led-row"><span className="led-k">On the rail since</span><span className="led-v">{fmtDate(g.purchased_on || g.created_at.slice(0, 10))}</span></div>
          )}
        </div>
        <div className="obj-actions">
          <button className={`wore-btn ${wore ? "logged" : ""}`} onClick={onWear}>
            {wore ? <><Check size={17} /> Logged</> : "Wore it"}
          </button>
        </div>
      </div>
      {viewing && g.photo_ref && (
        <PhotoViewer photos={[{ src: g.photo_ref, alt: g.item }]} onClose={() => setViewing(false)} />
      )}
      {form}
      {confirm}
    </main>
  );
}

/* ── R2 · the truth lede: one big figure ── */
function TruthLede({ garments, totalWears, signals }: { garments: Garment[]; totalWears: number; signals: ClosetSignals | null }) {
  const totalSpend = garments.reduce((a, g) => a + g.price_cents, 0);
  const avgCpw = totalWears > 0 && totalSpend > 0 ? Math.round(totalSpend / totalWears) : null;
  const neverWorn = garments.filter((g) => g.wear_count === 0).length;
  return (
    <div className="truth-lede">
      <div className="truth-fig">{avgCpw !== null ? formatCents(avgCpw) : "—"}</div>
      <p className="truth-cap">
        {avgCpw !== null
          ? `Average cost per wear across ${garments.length === 1 ? "one piece" : `${garments.length} pieces`}.${neverWorn > 0 ? ` ${neverWorn === 1 ? "One of them has" : `${neverWorn} of them have`} never left the rail.` : ""}`
          : "Log prices and wears and the number appears."}
      </p>
    </div>
  );
}

// READY-STATE 2026-09-19 — this was the rail illustration, a sentence and one pill, so the loop
// that IS the promise (DESIGN.md:43 "Know what you actually wear"; :73 the wear → cost-per-wear
// loop) was described rather than shown, and the only door was the full garment form. The ledger
// is now drawn with its zeros in it, and one line puts a real piece on the rail with today's wear
// already logged — which is what makes cost-per-wear a number instead of a promise.
// ⚠️ GENERAL: anyone's wardrobe. Nothing here is written for a woman or a man.
// ⚠️ No photo is asked for — the cut-out is iOS Vision, on-device and NATIVE-only (row 15), so it
// is never implied here. The weather outfit (row 13) needs a location prompt and lives on Style;
// forcing a permission into minute one would be the wrong trade, so it is not pulled forward.
function EmptyCloset({ onAdd, onStarted }: { onAdd: () => void; onStarted?: () => void | Promise<void> }) {
  return (
    <div className="fade-up" style={{ padding: "2.2rem 1.25rem 2rem" }}>
      <div className="empty-art" style={{ width: 150, height: "auto", margin: "0 auto" }}><img src={momentEmptyCloset} alt="" /></div>
      <div className="serif ocl-first-head">Nothing on the rail yet</div>

      <div className="ocl-ledger" aria-label="What this works out once pieces are on the rail">
        <div className="ocl-led"><span className="ocl-led-n tnum">0</span><span className="ocl-led-k">worn</span></div>
        <div className="ocl-led"><span className="ocl-led-n tnum">—</span><span className="ocl-led-k">cost per wear</span></div>
        <div className="ocl-led"><span className="ocl-led-n tnum">0</span><span className="ocl-led-k">never worn</span></div>
      </div>
      <p className="ocl-first-lede">
        Tap <strong>Wore it</strong> each time you wear something and these fill in — what a piece
        has actually cost you per wearing, and what has been hanging there untouched.
      </p>

      {onStarted && <FirstPiece onStarted={onStarted} />}

      <div style={{ textAlign: "center", marginTop: onStarted ? "0.8rem" : "1.4rem" }}>
        <button className={onStarted ? "ocl-first-alt" : "btn btn-primary"}
          style={onStarted ? undefined : { margin: "0 auto", minHeight: 52, padding: "0 1.4rem" }}
          onClick={onAdd}>
          {onStarted ? "Add with brand, colour and a photo" : <><Plus size={17} /> Add the first piece</>}
        </button>
      </div>

      <p className="ocl-first-foot">
        Everything stays on this device — a backup goes to your own cloud, not ours — and the app
        itself is free, with nothing to subscribe to.
      </p>
    </div>
  );
}

// The one-line door: a piece, what it cost, and whether it is on you today.
function FirstPiece({ onStarted }: { onStarted: () => void | Promise<void> }) {
  const [item, setItem] = useState("");
  const [price, setPrice] = useState("");
  const [woreIt, setWoreIt] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function add() {
    const t = item.trim();
    if (!t) { setErr("Name the piece — brand, colour and a photo can come later."); return; }
    setBusy(true); setErr(null);
    try {
      const cents = Math.round((Number(price.replace(/[^0-9.]/g, "")) || 0) * 100);
      const g = await store.createGarment({ item: t, price_cents: cents > 0 ? cents : 0 });
      // Logging today's wear with it is what turns cost-per-wear from a promise into a figure.
      if (woreIt) await store.logWear(g.id, todayLocal());
      await onStarted();
    } catch (e) {
      setErr(`Couldn't add that — ${e instanceof Error ? e.message : String(e)}`);
      setBusy(false);
    }
  }

  return (
    <div className="ocl-first">
      <div className="ocl-first-rule"><span>Put one on the rail</span></div>
      <div className="ocl-first-row">
        <input value={item} onChange={(e) => setItem(e.target.value)} aria-label="The piece"
          placeholder="Piece — e.g. navy wool coat" className="ocl-first-item"
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void add(); } }} />
        <input value={price} onChange={(e) => setPrice(e.target.value)} aria-label="What it cost"
          inputMode="decimal" placeholder="cost" className="ocl-first-price"
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void add(); } }} />
      </div>
      <label className="ocl-first-check">
        <input type="checkbox" checked={woreIt} onChange={(e) => setWoreIt(e.target.checked)} />
        <span>Wore it today</span>
      </label>
      {err && <div className="ocl-first-err">{err}</div>}
      <button className="btn btn-primary ocl-first-go" disabled={busy} onClick={() => void add()}>
        {busy ? "Adding…" : "Put it on the rail"}
      </button>
      <p className="ocl-first-hint">
        Leave the cost blank and everything still works — cost per wear just stays blank until you
        add one.
      </p>
    </div>
  );
}

/** THE RACK — what is and isn't earning its keep (ledger row 2b).
 *
 *  signals.rs already derives, per garment, `cost_per_wear_cents`, `last_worn_on` and
 *  `days_dormant`, plus a whole `dormant` list — and the wardrobe was ordered
 *  `ORDER BY created_at DESC`, i.e. newest-added first. So the screen answered "what did I buy
 *  recently" when the question a wardrobe app exists to answer is "what am I actually wearing, and
 *  what is sitting there costing me?". Cost-per-wear IS the wardrobe metric — the whole buy-less-
 *  wear-more idea — and it was computed and then not led with.
 *
 *  Two bands, because they are different situations: something worn a lot is EARNING its keep
 *  (best value first, the reassuring half); something bought and not worn is the decision.
 *
 *  🔒 Descriptive: these are the wears the person logged and the price they entered. No judgement
 *  about their spending, no suggestion to buy or get rid of anything. */
function WearRack({ signals, garments }: { signals: ClosetSignals | null; garments?: Garment[] }) {
  if (!signals) return null;
  const photoById = new Map((garments ?? []).map((g) => [g.id, g] as const));
  const thumb = (id: string | undefined, category?: string) => {
    const g = id ? photoById.get(id) : undefined;
    return g?.photo_ref
      ? <img className="rack-thumb" src={g.photo_ref} alt="" />
      : <span className="rack-thumb" style={{ display: "grid", placeItems: "center" }}><img src={glyphFor(category ?? g?.category ?? "")} alt="" style={{ width: "60%" }} /></span>;
  };
  const withCpw = signals.items.filter((i) => i.cost_per_wear_cents !== null && i.wears > 0);
  const best = [...withCpw].sort((a, b) => (a.cost_per_wear_cents ?? 0) - (b.cost_per_wear_cents ?? 0)).slice(0, 3);
  // Never-worn first (days_dormant is null), then longest-dormant.
  const idle = [...signals.dormant]
    .sort((a, b) => (a.days_dormant === null ? -1 : b.days_dormant === null ? 1 : (b.days_dormant ?? 0) - (a.days_dormant ?? 0)))
    .slice(0, 3);
  if (!best.length && !idle.length) return null;

  return (
    <section className="rack fade-up" aria-label="What you wear and what sits idle">
      {best.length > 0 && (
        <div className="rack-band">
          <div className="rack-title">Earning its keep</div>
          {best.map((i) => (
            <div key={i.id} className="rack-row">
              {thumb(i.id)}
              <span className="rack-item">{i.item}</span>
              <span className="rack-fig tnum">{formatCents(i.cost_per_wear_cents ?? 0)}<span className="rack-unit"> /wear</span></span>
              <span className="rack-sub muted">{i.wears} wear{i.wears === 1 ? "" : "s"}{i.brand ? ` · ${i.brand}` : ""}</span>
            </div>
          ))}
        </div>
      )}
      {withCpw.length > 1 && (
        <div className="rack-band">
          <div className="rack-title">Costing you the most</div>
          {[...withCpw].sort((a, b) => (b.cost_per_wear_cents ?? 0) - (a.cost_per_wear_cents ?? 0)).slice(0, 3).map((i) => (
            <div key={`w-${i.id}`} className="rack-row">
              {thumb(i.id)}
              <span className="rack-item">{i.item}</span>
              <span className="rack-fig tnum">{formatCents(i.cost_per_wear_cents ?? 0)}<span className="rack-unit"> /wear</span></span>
              <span className="rack-sub muted">{i.wears} wear{i.wears === 1 ? "" : "s"}{i.brand ? ` · ${i.brand}` : ""}</span>
            </div>
          ))}
        </div>
      )}
      {idle.length > 0 && (
        <div className="rack-band rack-idle">
          <div className="rack-title">Untouched</div>
          {idle.map((i, n) => (
            <div key={`${i.item}-${n}`} className="rack-row">
              {thumb((i as { id?: string }).id, i.category)}
              <span className="rack-item">{i.item}</span>
              <span className="rack-fig tnum muted">
                {i.days_dormant === null ? "never" : `${i.days_dormant}d`}
              </span>
              <span className="rack-sub muted">
                {i.days_dormant === null ? "no wears logged" : `last worn ${fmtDate(i.last_worn_on)}`}{i.category ? ` · ${i.category}` : ""}
              </span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function ClosetHero({ garments, totalWears }: { garments: Garment[]; totalWears: number }) {
  const total = garments.length;
  const totalSpend = useMemo(() => garments.reduce((a, g) => a + g.price_cents, 0), [garments]);
  const avgCpw = totalWears > 0 && totalSpend > 0 ? Math.round(totalSpend / totalWears) : null;
  return (
    <div className="hero fade-up" style={{ marginBottom: "1rem" }}>
      <div className="row" style={{ justifyContent: "space-between", alignItems: "flex-start", gap: "1rem", position: "relative", zIndex: 1 }}>
        <div>
          <div className="serif tnum" style={{ fontSize: "1.9rem", fontWeight: 600, lineHeight: 1.1, letterSpacing: "-0.02em" }}>
            {total} {total === 1 ? "item" : "items"}
          </div>
          <div style={{ fontSize: "var(--fs-sm)", opacity: 0.82, marginTop: 3 }}>
            {totalWears} {totalWears === 1 ? "wear" : "wears"} logged{totalWears > 0 ? "" : " yet"}
          </div>
        </div>
        {avgCpw !== null && (
          <div style={{ textAlign: "right" }}>
            <div className="tnum serif" style={{ fontSize: "1.5rem", fontWeight: 600 }}>{formatCents(avgCpw)}</div>
            <div style={{ fontSize: "var(--fs-xs)", opacity: 0.72 }}>avg cost / wear</div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Insights panel: the computed-figures dashboard (deterministic, no AI call) ──
function InsightsPanel({ garments }: { garments: Garment[] }) {
  const [signals, setSignals] = useState<ClosetSignals | null>(null);
  const [loading, setLoading] = useState(true);

  // Recompute whenever the wardrobe changes (count + total wears as a cheap dep).
  const totalWears = useMemo(() => garments.reduce((a, g) => a + g.wear_count, 0), [garments]);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    store.computeSignals(todayLocal())
      .then((s) => { if (alive) { setSignals(s); setLoading(false); } })
      .catch(() => { if (alive) { setSignals(null); setLoading(false); } });
    return () => { alive = false; };
  }, [garments.length, totalWears]);

  if (loading) {
    return (
      <div className="card" style={{ padding: "1rem", marginBottom: "1rem" }}>
        <div className="stat-grid">
          {[0, 1, 2, 3].map((i) => <div key={i} className="skeleton-block" style={{ height: 76 }} />)}
        </div>
      </div>
    );
  }
  if (!signals) return null;

  const o = signals.overall;
  const a = signals.activity;
  const deployedPct = o.value_deployed_ratio !== null ? Math.round(o.value_deployed_ratio * 100) : null;

  return (
    <div className="fade-up" style={{ marginBottom: "1.1rem" }}>
      <div className="stat-grid" style={{ marginBottom: "0.7rem" }}>
        <StatCard
          label={<><Coins size={13} /> Avg cost / wear</>}
          value={o.avg_cost_per_wear_cents !== null ? formatCents(o.avg_cost_per_wear_cents) : "—"}
          sub={o.avg_cost_per_wear_cents === null ? "Log a few wears" : `${o.total_wears} wear${o.total_wears === 1 ? "" : "s"} total`}
        />
        <StatCard
          label={<><CalendarClock size={13} /> Worn this month</>}
          value={String(a.wears_last_30d)}
          unit={a.items_worn_last_30d > 0 ? `· ${a.items_worn_last_30d} item${a.items_worn_last_30d === 1 ? "" : "s"}` : undefined}
          trend={a.direction !== "flat" && a.wears_prev_30d > 0
            ? { good: a.direction === "up", label: `${a.delta_30d > 0 ? "+" : ""}${a.delta_30d} vs last` }
            : null}
          sub={a.wears_last_30d === 0 ? "No wears in 30 days" : undefined}
        />
        <StatCard
          label={<><Wallet size={13} /> Value worn</>}
          value={deployedPct !== null ? `${deployedPct}%` : "—"}
          sub={deployedPct === null ? "Add prices to track" : (o.never_worn_spend_cents > 0 ? `${formatCents(o.never_worn_spend_cents)} unworn` : "of spend in rotation")}
        />
        <StatCard
          label={<><Layers size={13} /> Never worn</>}
          value={String(o.items_never_worn)}
          unit={o.total_items > 0 ? `/ ${o.total_items}` : undefined}
          sub={o.items_never_worn === 0 ? "all items worn" : "no logged wears"}
        />
      </div>

      {signals.categories.some((c) => c.wears > 0) && <CategoryBalance signals={signals} />}
      {signals.seasons.some((s) => s.wears > 0) && <SeasonStrip signals={signals} />}
    </div>
  );
}

function StatCard({ label, value, unit, trend, sub }: {
  label: React.ReactNode; value: string; unit?: string;
  trend?: { good: boolean; label: string } | null; sub?: string;
}) {
  return (
    <div className="stat">
      <div className="stat-lbl">{label}</div>
      <div className="stat-val tnum">{value}{unit && <span className="unit">{unit}</span>}</div>
      {trend && trend.label ? (
        <div className="stat-sub tnum" style={{ color: trend.good ? "var(--good)" : "var(--bad)" }}>{trend.label}</div>
      ) : sub ? (
        <div className="stat-sub tnum">{sub}</div>
      ) : null}
    </div>
  );
}

// Category balance — share of wears per category, computed in Rust (descriptive only).
function CategoryBalance({ signals }: { signals: ClosetSignals }) {
  const rows = signals.categories.filter((c) => c.wears > 0).slice(0, 6);
  if (rows.length === 0) return null;
  const max = Math.max(...rows.map((r) => r.share_of_wears), 0.01);
  return (
    <div className="card" style={{ padding: "0.9rem 1rem", marginBottom: "0.7rem" }}>
      <div className="stat-lbl" style={{ marginBottom: "0.6rem" }}>Category balance · share of wears</div>
      {rows.map((r) => (
        <div className="cat-row" key={r.category}>
          <span className="cat-name">{titleCase(r.category)}</span>
          <div className="cat-track"><div className="cat-fill" style={{ width: `${Math.max(6, Math.round((r.share_of_wears / max) * 100))}%` }} /></div>
          <span className="cat-val tnum">{Math.round(r.share_of_wears * 100)}%</span>
        </div>
      ))}
    </div>
  );
}

// Season strip — which season the closet is used most (descriptive, computed in Rust).
function SeasonStrip({ signals }: { signals: ClosetSignals }) {
  const order: ClosetSignals["seasons"] = ["winter", "spring", "summer", "autumn"].map(
    (s) => signals.seasons.find((x) => x.season === s) ?? { season: s as "winter", wears: 0, share_of_wears: 0 }
  );
  const peak = Math.max(...order.map((s) => s.wears), 0);
  return (
    <div className="card" style={{ padding: "0.9rem 1rem" }}>
      <div className="stat-lbl" style={{ marginBottom: "0.7rem" }}>Season usage</div>
      <div className="season-strip">
        {order.map((s) => {
          const isPeak = peak > 0 && s.wears === peak;
          const h = peak > 0 ? Math.max(4, Math.round((s.wears / peak) * 34)) : 4;
          return (
            <div key={s.season} className={`season-cell${isPeak ? " peak" : ""}`} title={`${s.wears} wear${s.wears === 1 ? "" : "s"} in ${s.season}`}>
              <div className="season-bar-wrap"><div className="season-bar" style={{ height: h }} /></div>
              <div className="season-pct tnum">{Math.round(s.share_of_wears * 100)}%</div>
              <div className="season-lbl">{s.season}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── The billed AI narration: Closet Recap (pure inventory description, no advice) ──
type RecapErr = { code: BrainErrorCode | "empty"; message: string };

function ClosetRecapCard({ garments }: { garments: Garment[] }) {
  const [text, setText] = useState<string | null>(null);
  const [signals, setSignals] = useState<ClosetSignals | null>(null);
  const [busy, setBusy] = useState(false);
  const [fresh, setFresh] = useState(false);
  const [err, setErr] = useState<RecapErr | null>(null);

  // Enough to recap = at least 1 item with at least 1 logged wear (so there's a cost-per-wear
  // figure to narrate). Gated instantly off the in-memory list; the actual recap sends the
  // DERIVED-SIGNALS object computed on-device (signals.rs).
  const totalWears = useMemo(() => garments.reduce((a, g) => a + g.wear_count, 0), [garments]);
  const canRecap = garments.length > 0 && totalWears > 0;

  async function run() {
    setBusy(true); setErr(null); setText(null); setSignals(null); setFresh(false);
    try {
      const sig = await store.computeSignals(todayLocal());
      const r = await closetRecap(sig);
      const recap = r.recap.trim();
      if (!recap) {
        setErr({ code: "empty", message: "Not enough logged wears to recap yet — tap “Wore it” a few times." });
      } else {
        setText(recap);
        setSignals(sig);
        setFresh(true);
        setTimeout(() => setFresh(false), 700);
      }
    } catch (e) {
      if (e instanceof BrainError) setErr({ code: e.code, message: e.message });
      else setErr({ code: "unavailable", message: "Recap failed — please try again." });
    } finally { setBusy(false); }
  }

  if (garments.length === 0) return null;
  return (
    <div className={`recap${fresh ? " fresh" : ""}`} style={{ padding: "1.1rem 1.25rem", marginBottom: "1rem" }}>
      <div className="row" style={{ justifyContent: "space-between", gap: "0.6rem" }}>
        <div className="eyebrow" style={{ flexWrap: "wrap" }}>
          <Sparkles size={14} style={{ flexShrink: 0 }} /> The read
        </div>
        <button className="btn btn-primary btn-sm btn-pill" disabled={busy || !canRecap} onClick={run} style={{ flexShrink: 0 }}>
          {busy ? "Reading…" : text ? (<><RefreshCw size={12} /> Refresh</>) : "Read it"}
        </button>
      </div>

      {!busy && !text && !err && canRecap && (
        <div className="muted" style={{ fontSize: "var(--fs-sm)", marginTop: 8, lineHeight: 1.55 }}>
          A plain-words read of your cost-per-wear, dormant items, category balance and season usage — facts only, no advice.
        </div>
      )}
      {!busy && !text && !err && !canRecap && (
        <div className="muted" style={{ fontSize: "var(--fs-sm)", marginTop: 8 }}>Log a few wears and the read unlocks.</div>
      )}

      {busy && (
        <div aria-live="polite" style={{ marginTop: "0.85rem", display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 9 }}>
          <span className="muted row" style={{ fontSize: "var(--fs-xs)", gap: "0.4rem" }}>
            <Sparkles size={12} style={{ color: "var(--accent)" }} className="pulse-soft" /> Tallying your closet…
          </span>
          <div className="skeleton-line" style={{ width: "100%" }} />
          <div className="skeleton-line" style={{ width: "94%" }} />
          <div className="skeleton-line" style={{ width: "68%" }} />
          {/* Reserve the facts column too, so the result lands without a layout shift. */}
          <div style={{ marginTop: 6, paddingTop: 8, borderTop: "1px solid var(--border-subtle)", display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 8 }}>
            <div className="skeleton-line" style={{ width: "82%", height: 9 }} />
            <div className="skeleton-line" style={{ width: "74%", height: 9 }} />
          </div>
        </div>
      )}

      {err && !busy && <BrainErr err={err} onRetry={run} />}

      {text && !busy && (
        <div className="fade-up" style={{ marginTop: "0.7rem" }} aria-live="polite">
          <p style={{ fontSize: "var(--fs-md)", color: "var(--ink-1)", lineHeight: 1.65, margin: 0, whiteSpace: "pre-wrap" }}>{text}</p>
          <div style={{ marginTop: 8 }}><OrioraBadge /></div>
          {signals && <FactList signals={signals} />}
          <div className="row" style={{ marginTop: 12, justifyContent: "space-between", gap: "0.6rem" }}>
            <span className="muted" style={{ fontSize: "var(--fs-xxs)", flex: 1, minWidth: 0 }}>Describes the numbers you’ve logged — not style, shopping or any other advice.</span>
            <CopyButton text={copyableNarration(text, signals)} />
          </div>
        </div>
      )}
    </div>
  );
}

// "Ask your closet" — repeat billable Q&A over the same on-device signal slice.
function AskClosetCard({ garments }: { garments: Garment[] }) {
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<RecapErr | null>(null);

  const totalWears = useMemo(() => garments.reduce((a, g) => a + g.wear_count, 0), [garments]);
  const canAsk = garments.length > 0 && totalWears > 0;
  const suggestions = [
    "What's my priciest item per wear?",
    "What haven't I worn this season?",
    "How much have I worn in the last month?",
  ];

  async function ask(q: string) {
    const text = q.trim();
    if (!text) return;
    setBusy(true); setErr(null); setAnswer(null);
    try {
      const sig = await store.computeSignals(todayLocal());
      const r = await askCloset(sig, text);
      setAnswer(r.answer || "I couldn't find that in your logged data.");
    } catch (e) {
      if (e instanceof BrainError) setErr({ code: e.code, message: e.message });
      else setErr({ code: "unavailable", message: "That failed — please try again." });
    } finally { setBusy(false); }
  }

  if (!canAsk) return null;
  return (
    <div className="card" style={{ padding: "1.1rem 1.25rem", marginBottom: "1.25rem" }}>
      <div className="eyebrow" style={{ marginBottom: "0.7rem" }}>
        <MessageCircleQuestion size={15} /> Ask your closet
      </div>
      <form className="row" style={{ gap: "0.5rem", flexWrap: "nowrap" }} onSubmit={(e) => { e.preventDefault(); void ask(question); }}>
        <input placeholder="Ask about the clothes you own…" value={question} onChange={(e) => setQuestion(e.target.value)} />
        <button type="submit" className="btn btn-primary btn-pill" style={{ flexShrink: 0, padding: "0 1.1rem" }} disabled={busy || !question.trim()}>{busy ? "…" : "Ask"}</button>
      </form>

      {!answer && !err && !busy && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", marginTop: "0.7rem" }}>
          {suggestions.map((s) => (
            <button key={s} className="chip chip-suggest" onClick={() => { setQuestion(s); void ask(s); }}>{s}</button>
          ))}
        </div>
      )}

      {busy && (
        <div aria-live="polite" style={{ marginTop: "0.8rem", display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 9 }}>
          <div className="skeleton-line" style={{ width: "100%" }} />
          <div className="skeleton-line" style={{ width: "78%" }} />
        </div>
      )}

      {err && !busy && <BrainErr err={err} onRetry={() => void ask(question)} />}

      {answer && !busy && (
        <div className="fade-up" style={{ marginTop: "0.8rem" }} aria-live="polite">
          <p style={{ fontSize: "var(--fs-md)", color: "var(--ink-1)", lineHeight: 1.65, margin: 0, whiteSpace: "pre-wrap" }}>{answer}</p>
          <div style={{ marginTop: 8 }}><OrioraBadge /></div>
          <div className="row" style={{ marginTop: 10, justifyContent: "space-between", gap: "0.6rem" }}>
            <span className="muted" style={{ fontSize: "var(--fs-xxs)", flex: 1, minWidth: 0 }}>Answers from your logged figures only — not style, shopping or any other advice.</span>
            <CopyButton text={answer} />
          </div>
        </div>
      )}
    </div>
  );
}

// ── Device dictation (free native voice, APP-READY §1) ─────────────────────────
// Speak-in stays FREE + on-device: on iOS the keyboard mic dictates into any input (nothing to
// add); where the webview exposes the Web Speech API (macOS/web) we render a mic button too.
// Audio never leaves the device — recognition produces TEXT that feeds the normal AI call.
function useDictation(onText: (t: string) => void) {
  const Ctor = typeof window !== "undefined"
    ? ((window as unknown as Record<string, unknown>).SpeechRecognition ?? (window as unknown as Record<string, unknown>).webkitSpeechRecognition)
    : undefined;
  const supported = typeof Ctor === "function";
  const [listening, setListening] = useState(false);
  const recRef = useRef<{ stop: () => void } | null>(null);
  const onTextRef = useRef(onText);
  onTextRef.current = onText;
  const start = useCallback(() => {
    if (typeof Ctor !== "function") return;
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const rec = new (Ctor as any)();
      rec.lang = navigator.language || "en-US";
      rec.interimResults = false;
      rec.maxAlternatives = 1;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      rec.onresult = (e: any) => {
        const t = e?.results?.[0]?.[0]?.transcript;
        if (typeof t === "string" && t.trim()) onTextRef.current(t.trim());
      };
      rec.onend = () => setListening(false);
      rec.onerror = () => setListening(false);
      recRef.current = rec;
      rec.start();
      setListening(true);
    } catch { setListening(false); }
  }, [Ctor]);
  const stop = useCallback(() => { try { recRef.current?.stop(); } catch { /* ignore */ } setListening(false); }, []);
  useEffect(() => () => { try { recRef.current?.stop(); } catch { /* ignore */ } }, []);
  return { supported, listening, start, stop };
}

// ── "Outfit ideas" — the styling upgrade (operator-approved 2026-07-02) ────────
// Composes 2-3 outfits STRICTLY from garments the user OWNS. ONE billed call; only the text
// identity of the garments (+ the occasion note) leaves the device — photo thumbnails are
// joined back HERE by id, client-side. Never buy/keep/donate, shopping, or body talk.
function OutfitCard({ garments, onSaved }: { garments: Garment[]; onSaved?: () => void }) {
  const [occasion, setOccasion] = useState("");
  const [result, setResult] = useState<OutfitResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<RecapErr | null>(null);
  const [savedIdx, setSavedIdx] = useState<Set<number>>(new Set());
  const [weather, setWeather] = useState<WeatherNow | null>(null);

  const byId = useMemo(() => new Map(garments.map((g) => [g.id, g])), [garments]);
  const canStyle = garments.length >= 2;
  const suggestions = ["Casual weekend", "Office day", "Dinner out", "Cold and rainy"];

  const dict = useDictation((t) => setOccasion((cur) => (cur ? `${cur} ${t}` : t)));

  // "Dress for today" (parity #13) — the leader's headline feature. Weather is fetched ON-DEVICE
  // (Open-Meteo, free, no key); only a short summary reaches the brain, never a location. If the
  // customer declines location or it fails, `w` is null and this behaves exactly as it always did.
  async function runToday() {
    setBusy(true); setErr(null); setResult(null); setWeather(null);
    try {
      const w = await getWeather();
      setWeather(w);
      const r = await suggestOutfits(garments, occasion.trim(), w);
      setResult(r);
    } catch (e) {
      if (e instanceof BrainError) setErr({ code: e.code, message: e.message });
      else setErr({ code: "unavailable", message: "That failed — please try again." });
    } finally { setBusy(false); }
  }

  async function run(occ: string) {
    setBusy(true); setErr(null); setResult(null); setWeather(null);
    try {
      const r = await suggestOutfits(garments, occ);
      setResult(r);
    } catch (e) {
      if (e instanceof BrainError) setErr({ code: e.code, message: e.message });
      else setErr({ code: "unavailable", message: "That failed — please try again." });
    } finally { setBusy(false); }
  }

  async function saveLook(idx: number, name: string, ids: string[]) {
    if (savedIdx.has(idx) || ids.length === 0) return;
    try {
      await store.createCollection({ kind: "outfit", name: name || "Saved look", garment_ids: JSON.stringify(ids), occasion: occasion.trim() });
      setSavedIdx((s) => new Set(s).add(idx));
      onSaved?.();
    } catch { /* transient — leave the button tappable to retry */ }
  }

  if (!canStyle) return null;

  // Resolve each outfit's ids to live garments (an item deleted since the call is skipped).
  const resolved = (result?.outfits ?? [])
    .map((o) => ({ ...o, items: o.garment_ids.map((id) => byId.get(id)).filter((g): g is Garment => Boolean(g)) }))
    .filter((o) => o.items.length > 0);

  return (
    <div className="card" style={{ padding: "1.1rem 1.25rem", marginBottom: "1.25rem" }}>
      <div className="eyebrow" style={{ marginBottom: "0.7rem" }}>
        <Wand2 size={15} /> Outfit ideas
      </div>
      <form className="row" style={{ gap: "0.5rem", flexWrap: "nowrap" }} onSubmit={(e) => { e.preventDefault(); void run(occasion); }}>
        <input placeholder="Occasion, weather, preferences… (optional)" value={occasion} onChange={(e) => setOccasion(e.target.value)} />
        {dict.supported && (
          <button type="button" className="tile-act" aria-label={dict.listening ? "Stop dictation" : "Dictate the occasion"}
            title={dict.listening ? "Stop dictation" : "Dictate"} aria-pressed={dict.listening}
            style={{ flexShrink: 0, ...(dict.listening ? { color: "var(--accent)" } : {}) }}
            onClick={() => (dict.listening ? dict.stop() : dict.start())}>
            <Mic size={16} className={dict.listening ? "pulse-soft" : undefined} />
          </button>
        )}
        <button type="submit" className="btn btn-primary btn-pill" style={{ flexShrink: 0, padding: "0 1.1rem" }} disabled={busy}>
          {busy ? "…" : "Style me"}
        </button>
      </form>

      {/* Dress for today — weather-aware outfits (parity #13). Sits above the presets because it is
          the one-tap daily loop the category is bought for. */}
      {!busy && (
        <div style={{ marginTop: "0.6rem" }}>
          <button type="button" className="btn btn-pill" onClick={() => void runToday()} disabled={busy}
            style={{ width: "100%", minHeight: 40 }}>
            <Sparkles size={14} /> Dress for today’s weather
          </button>
        </div>
      )}
      {weather && !busy && (
        <div className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: "0.45rem", lineHeight: 1.5 }}>
          {Math.round(weather.temp_c)}°C now · {Math.round(weather.low_c)}–{Math.round(weather.high_c)}°C today
          {weather.summary ? ` · ${weather.summary}` : ""}
          {weather.rain_chance >= 20 ? ` · ${Math.round(weather.rain_chance)}% chance of rain` : ""}
        </div>
      )}

      {!result && !err && !busy && (
        <>
          <div className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: "0.6rem", lineHeight: 1.5 }}>
            2-3 outfits put together from clothes you already own — nothing to buy.
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", marginTop: "0.55rem" }}>
            {suggestions.map((s) => (
              <button key={s} className="chip chip-suggest" onClick={() => { setOccasion(s); void run(s); }}>{s}</button>
            ))}
          </div>
        </>
      )}

      {busy && (
        <div aria-live="polite" style={{ marginTop: "0.8rem", display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 9 }}>
          <span className="muted row" style={{ fontSize: "var(--fs-xs)", gap: "0.4rem" }}>
            <Sparkles size={12} style={{ color: "var(--accent)" }} className="pulse-soft" /> Putting pieces together…
          </span>
          <div className="skeleton-block" style={{ height: 74 }} />
          <div className="skeleton-block" style={{ height: 74 }} />
        </div>
      )}

      {err && !busy && <BrainErr err={err} onRetry={() => void run(occasion)} />}

      {result && !busy && (
        <div className="fade-up" style={{ marginTop: "0.85rem" }} aria-live="polite">
          {resolved.map((o, i) => (
            <div key={i} style={{ padding: "0.75rem 0", borderTop: i === 0 ? "none" : "1px solid var(--border-subtle)" }}>
              <div style={{ fontWeight: 600, fontSize: "var(--fs-sm)", marginBottom: "0.5rem" }}>{o.name}</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", marginBottom: "0.45rem" }}>
                {o.items.map((g) => (
                  <div key={g.id} className="row" style={{ gap: "0.4rem", flexWrap: "nowrap" }}>
                    {g.photo_ref ? (
                      <img src={g.photo_ref} alt="" style={{ width: 40, height: 40, borderRadius: 9, objectFit: "cover", flexShrink: 0, border: "1px solid var(--border-subtle)" }} />
                    ) : (
                      <div className="swatch" style={{ width: 40, height: 40, borderRadius: 9, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, background: cssColor(g.color) ?? "var(--elev-2)" }} aria-hidden>
                        {cssColor(g.color) ? null : <Shirt size={15} />}
                      </div>
                    )}
                    <span style={{ fontSize: "var(--fs-xs)", maxWidth: 110, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{g.item}</span>
                  </div>
                ))}
              </div>
              {o.why && <p className="muted" style={{ fontSize: "var(--fs-xs)", lineHeight: 1.55, margin: 0 }}>{o.why}</p>}
              <button className="btn btn-sm btn-pill" style={{ marginTop: 8 }} disabled={savedIdx.has(i)}
                onClick={() => void saveLook(i, o.name, o.items.map((g) => g.id))}>
                {savedIdx.has(i) ? "✓ Saved to your looks" : "＋ Save this look"}
              </button>
            </div>
          ))}
          {resolved.length === 0 && (
            <p className="muted" style={{ fontSize: "var(--fs-sm)", lineHeight: 1.55, margin: 0 }}>
              {result.note || "Couldn't put an outfit together from what's logged — add a few more pieces and try again."}
            </p>
          )}
          {resolved.length > 0 && result.note && (
            <p className="muted" style={{ fontSize: "var(--fs-xs)", lineHeight: 1.55, margin: "0.35rem 0 0" }}>{result.note}</p>
          )}
          <div style={{ marginTop: 8 }}><OrioraBadge /></div>
          <div className="row" style={{ marginTop: 10, justifyContent: "space-between", gap: "0.6rem" }}>
            <span className="muted" style={{ fontSize: "var(--fs-xxs)", flex: 1, minWidth: 0 }}>Styled only from clothes you own — never shopping, keep-or-donate or body talk.</span>
            {resolved.length > 0 && <CopyButton text={copyableOutfits(resolved.map((o) => ({ name: o.name, items: o.items.map((g) => g.item), why: o.why })))} />}
          </div>
        </div>
      )}
    </div>
  );
}

// Plain-text copy payload for the outfit suggestions (no markup).
function copyableOutfits(outfits: { name: string; items: string[]; why: string }[]): string {
  return outfits.map((o) => `${o.name}\n${o.items.map((i) => `• ${i}`).join("\n")}${o.why ? `\nWhy: ${o.why}` : ""}`).join("\n\n");
}

// Saved looks & packing lists — the "plan & keep" layer over the AI styling.
// Outfits saved from "Style me" and hand-built packing lists live on-device (collections
// table) so the user can re-wear them (one tap logs a wear for every piece) or pack a trip.
// Deleting a look never touches the garments; a garment removed from the closet just drops
// out of the look (ids resolve against the live wardrobe).
// Outfit calendar (parity #14) — plain date maths on an ISO date. Free: no AI, no billing.
// Competitors (Stylebook) sell an outfit calendar; ours reuses the saved looks already on-device.
function planLabel(iso: string): string {
  const d = new Date(iso + "T00:00:00");
  if (Number.isNaN(d.getTime())) return "";
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const days = Math.round((d.getTime() - today.getTime()) / 86_400_000);
  if (days < 0) return `was ${d.toLocaleDateString(undefined, { day: "numeric", month: "short" })}`;
  if (days === 0) return "today";
  if (days === 1) return "tomorrow";
  if (days <= 6) return d.toLocaleDateString(undefined, { weekday: "long" });
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

function SavedLooks({ garments, refreshKey, onChanged }: { garments: Garment[]; refreshKey: number; onChanged: () => void }) {
  const [looks, setLooks] = useState<Collection[]>([]);
  const [building, setBuilding] = useState(false);
  const [name, setName] = useState("");
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [woreId, setWoreId] = useState<string | null>(null);
  const byId = useMemo(() => new Map(garments.map((g) => [g.id, g])), [garments]);

  const refresh = useCallback(() => { void store.listCollections().then(setLooks).catch(() => {}); }, []);
  useEffect(() => { refresh(); }, [refresh, refreshKey]);

  async function wore(c: Collection, ids: string[]) {
    if (ids.length === 0) return;
    try {
      for (const id of ids) await store.logWear(id);
      setWoreId(c.id);
      onChanged();
    } catch { /* transient */ }
  }
  async function del(id: string) {
    try { await store.deleteCollection(id); setLooks((l) => l.filter((x) => x.id !== id)); } catch { /* transient */ }
  }

  async function planFor(id: string, iso: string) {
    // Optimistic — the write is a local SQLite UPDATE; a failure just leaves the old value.
    setLooks((l) => l.map((x) => (x.id === id ? { ...x, planned_on: iso } : x)));
    try { await store.setCollectionPlan(id, iso); } catch { /* transient */ }
  }
  async function saveList() {
    const ids = [...picked];
    if (ids.length === 0) return;
    try {
      await store.createCollection({ kind: "packing", name: name.trim() || "Packing list", garment_ids: JSON.stringify(ids) });
      setBuilding(false); setName(""); setPicked(new Set());
      refresh(); onChanged();
    } catch { /* transient */ }
  }

  // Nothing to plan with until there are a couple of pieces logged.
  if (garments.length < 2) return null;

  return (
    <section className="card" style={{ padding: "1.1rem 1.25rem", marginBottom: "1.25rem" }} aria-label="Saved looks and lists">
      <div className="row" style={{ justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem", gap: "0.5rem" }}>
        <div className="row" style={{ gap: "0.45rem", alignItems: "center" }}>
          <Layers size={16} style={{ color: "var(--accent)" }} />
          <span style={{ fontWeight: 600, fontSize: "var(--fs-sm)" }}>Saved looks &amp; lists</span>
        </div>
        <button className="btn btn-sm btn-pill" onClick={() => setBuilding((b) => !b)}>{building ? "Cancel" : "＋ New list"}</button>
      </div>

      {building && (
        <div style={{ marginBottom: "1rem", display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "0.55rem" }}>
          <input placeholder="List name (e.g. Weekend in Rome)" value={name} onChange={(e) => setName(e.target.value)} aria-label="List name" />
          <div style={{ maxHeight: 210, overflowY: "auto", display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "0.15rem", border: "1px solid var(--border-subtle)", borderRadius: 10, padding: "0.5rem" }}>
            {garments.map((g) => {
              const on = picked.has(g.id);
              return (
                <label key={g.id} className="row" style={{ gap: "0.55rem", alignItems: "center", fontSize: "var(--fs-xs)", cursor: "pointer", padding: "0.2rem 0.15rem" }}>
                  <input type="checkbox" checked={on} onChange={(e) => setPicked((p) => { const n = new Set(p); if (e.target.checked) n.add(g.id); else n.delete(g.id); return n; })} />
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{g.item}{g.category ? ` · ${g.category}` : ""}</span>
                </label>
              );
            })}
          </div>
          <button className="btn btn-primary btn-pill btn-sm" disabled={picked.size === 0} onClick={() => void saveList()}>Save list ({picked.size})</button>
        </div>
      )}

      {looks.length === 0 && !building && (
        <p className="muted" style={{ fontSize: "var(--fs-xs)", lineHeight: 1.55, margin: 0 }}>
          Save an outfit from “Style me”, or build a packing list — they live here to re-wear or pack a trip.
        </p>
      )}

      {/* Coming up — the calendar made visible. Plain date maths, nothing billed. */}
      {(() => {
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const upcoming = looks
          .filter((c) => c.kind === "outfit" && c.planned_on && new Date(c.planned_on + "T00:00:00") >= today)
          .sort((a, b) => a.planned_on.localeCompare(b.planned_on))
          .slice(0, 3);
        if (upcoming.length === 0) return null;
        return (
          <div style={{ marginBottom: "0.85rem", padding: "0.55rem 0.7rem", borderRadius: 10, background: "var(--elev-2)" }}>
            <div className="muted" style={{ fontSize: "var(--fs-xxs)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "0.3rem" }}>Coming up</div>
            {upcoming.map((c) => (
              <div key={c.id} style={{ fontSize: "var(--fs-xs)", lineHeight: 1.6 }}>
                <strong>{planLabel(c.planned_on)}</strong> — {c.name || "Look"}
              </div>
            ))}
          </div>
        );
      })()}

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "0.7rem" }}>
        {looks.map((c) => {
          const items = collectionIds(c).map((id) => byId.get(id)).filter((g): g is Garment => Boolean(g));
          const isOutfit = c.kind === "outfit";
          return (
            <div key={c.id} style={{ borderTop: "1px solid var(--border-subtle)", paddingTop: "0.65rem" }}>
              <div className="row" style={{ justifyContent: "space-between", gap: "0.5rem", alignItems: "flex-start" }}>
                <div style={{ minWidth: 0 }}>
                  <span style={{ fontWeight: 600, fontSize: "var(--fs-sm)" }}>{c.name || (isOutfit ? "Look" : "Packing list")}</span>
                  <span className="muted" style={{ fontSize: "var(--fs-xxs)", marginLeft: 6 }}>
                    {isOutfit ? "outfit" : "list"} · {items.length} {items.length === 1 ? "piece" : "pieces"}{c.occasion ? ` · ${c.occasion}` : ""}{c.planned_on ? ` · ${planLabel(c.planned_on)}` : ""}
                  </span>
                </div>
                <div className="row" style={{ gap: "0.35rem", flexShrink: 0 }}>
                  {isOutfit && (
                    <button className="btn btn-sm" disabled={items.length === 0} onClick={() => void wore(c, items.map((g) => g.id))} title="Log a wear for every piece in this look">
                      {woreId === c.id ? "✓ Logged" : "Wore it"}
                    </button>
                  )}
                  {/* Outfit calendar (parity #14): plan a saved look for a date. Pure date storage —
                      no AI, no billing; the "what's coming up" line below is plain date maths. */}
                  {isOutfit && (
                    <input type="date" value={c.planned_on || ""} aria-label={`Plan ${c.name || "this look"} for a date`}
                      title="Plan this look for a day"
                      onChange={(e) => void planFor(c.id, e.target.value)}
                      style={{ fontSize: "var(--fs-xxs)", padding: "0.2rem 0.35rem", maxWidth: 132 }} />
                  )}
                  <button className="tile-act" aria-label="Delete" title="Delete" onClick={() => void del(c.id)}><Trash2 size={15} /></button>
                </div>
              </div>
              {items.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", marginTop: "0.5rem" }}>
                  {items.map((g) => (
                    g.photo_ref
                      ? <img key={g.id} src={g.photo_ref} alt={g.item} title={g.item} style={{ width: 36, height: 36, borderRadius: 8, objectFit: "cover", border: "1px solid var(--border-subtle)" }} />
                      : <span key={g.id} className="chip" title={g.item} style={{ fontSize: "var(--fs-xxs)", maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{g.item}</span>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function BrainErr({ err, onRetry }: { err: RecapErr; onRetry: () => void }) {
  // Insufficient funds — NO amounts shown (402 fence). On iOS we must NOT steer to an external
  // purchase (Guideline 3.1.1): point to the in-app Apple IAP top-up. Web/macOS (no IAP) → website.
  if (err.code === "insufficient_funds") {
    return (
      <div className="notice notice-warn fade-up" style={{ marginTop: 12 }}>
        <Coins size={16} style={{ color: "var(--warning)", flexShrink: 0, marginTop: 1 }} />
        <div>
          <div style={{ fontSize: "var(--fs-sm)", fontWeight: 600 }}>Your Oriora balance is empty</div>
          <div className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: 2 }}>
            {platform() === "ios"
              ? "Open the account menu (top-right) and tap Add funds to top up, then try again."
              : "Your Oriora balance is too low. Add funds, then try again."}
          </div>
        </div>
      </div>
    );
  }
  // BYOK gate — Managed AI blocked for this app; needs a vendor key.
  if (err.code === "managed_ai_blocked") {
    return (
      <div className="notice notice-info fade-up" style={{ marginTop: 12 }}>
        <Sparkles size={16} style={{ color: "var(--accent)", flexShrink: 0, marginTop: 1 }} />
        <div style={{ fontSize: "var(--fs-xs)" }}>{err.message}</div>
      </div>
    );
  }
  if (err.code === "unauthenticated") {
    return (
      <div className="notice notice-info fade-up" style={{ marginTop: 12 }}>
        <Lock size={16} style={{ color: "var(--accent)", flexShrink: 0, marginTop: 1 }} />
        <div style={{ fontSize: "var(--fs-xs)" }}>{err.message}</div>
      </div>
    );
  }
  // Empty result — gentle, no retry button (more data is the fix, not a retry).
  if (err.code === "empty") {
    return <div className="muted fade-up" style={{ fontSize: "var(--fs-sm)", marginTop: 12 }}>{err.message}</div>;
  }
  // Network / unavailable — recoverable, offer a retry.
  return (
    <div className="notice notice-err fade-up" style={{ marginTop: 12, justifyContent: "space-between", alignItems: "center" }}>
      <span style={{ fontSize: "var(--fs-sm)" }}>{err.message}</span>
      <button className="btn btn-sm btn-pill" style={{ flexShrink: 0 }} onClick={onRetry}>
        <RefreshCw size={12} /> Retry
      </button>
    </div>
  );
}

// The receipts: the pre-ranked deterministic facts the recap drew from. Purely observational.
function FactList({ signals }: { signals: ClosetSignals }) {
  const facts = signals.headline_facts.slice(0, 5);
  if (facts.length === 0) return null;
  const dotClass = (t: FactTone) => (t === "good" ? "good" : t === "watch" ? "watch" : "");
  return (
    <div style={{ marginTop: "0.85rem", paddingTop: "0.5rem", borderTop: "1px solid var(--border-subtle)" }}>
      <div className="stat-lbl" style={{ marginBottom: "0.1rem" }}>The numbers behind this</div>
      {facts.map((f, i) => (
        <div key={i} className="fact">
          <span className={`fact-dot ${dotClass(f.tone)}`} />
          <span className="fact-text">{f.text}</span>
        </div>
      ))}
    </div>
  );
}

// Ghost copy-button for AI results (DESIGN-SYSTEM §7.5 — every Oriora result is copyable).
// Swaps glyph → check + "Copied" for 1.5s; aria-live announces it.
function CopyButton({ text, label = "Copy" }: { text: string; label?: string }) {
  const [done, setDone] = useState(false);
  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text);
      setDone(true);
      setTimeout(() => setDone(false), 1500);
    } catch { /* clipboard blocked — silently no-op */ }
  }, [text]);
  return (
    <button className="copy-btn" onClick={copy} aria-label={done ? "Copied" : label} title={done ? "Copied" : label}>
      <span aria-hidden>{done ? <Check size={13} /> : <Copy size={13} />}</span>
      <span className="copy-btn-lbl">{done ? "Copied" : label}</span>
      <span aria-live="polite" className="sr-only">{done ? "Copied to clipboard" : ""}</span>
    </button>
  );
}

// Build the plain-text copy payload: the narration plus its grounding facts (no markup).
function copyableNarration(text: string, signals: ClosetSignals | null): string {
  const facts = signals?.headline_facts.slice(0, 5).map((f) => `• ${f.text}`).join("\n") ?? "";
  return facts ? `${text}\n\nThe numbers behind this:\n${facts}` : text;
}

function GarmentTile({ g, wore, onWear, onEdit, onDelete }: { g: Garment; wore: boolean; onWear: () => void; onEdit: () => void; onDelete: () => void }) {
  const [viewing, setViewing] = useState(false);
  const cpw = costPerWearCents(g);
  const meta = [
    g.brand ? g.brand : null,
    g.category ? titleCase(g.category) : null,
    g.color ? titleCase(g.color) : null,
  ].filter(Boolean).join(" · ");

  // The colour swatch uses a CSS named colour when it matches; otherwise a neutral fill.
  const swatchColor = cssColor(g.color);

  return (
    <div className="tile">
      {g.photo_ref ? (
        <img src={g.photo_ref} alt={g.item} className="swatch" title="Tap to view"
          onClick={() => setViewing(true)}
          style={{ objectFit: "cover", padding: 0, cursor: "zoom-in" }} />
      ) : (
        <div className="swatch" style={{ background: swatchColor ?? "var(--elev-2)" }} aria-hidden>
          {swatchColor ? null : <Shirt size={18} />}
        </div>
      )}
      {viewing && g.photo_ref && (
        <PhotoViewer photos={[{ src: g.photo_ref, alt: g.item }]} onClose={() => setViewing(false)} />
      )}
      <div style={{ flex: 1, minWidth: 0, cursor: "pointer" }} onClick={onEdit} role="button" tabIndex={0}
        onKeyDown={(e) => { if (e.key === "Enter") onEdit(); }}>
        <div style={{ fontWeight: 600, fontSize: "var(--fs-sm)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{g.item}</div>
        {meta && <div className="muted" style={{ fontSize: "var(--fs-xs)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{meta}</div>}
        <div className="row" style={{ gap: "0.4rem", marginTop: 3, fontSize: "var(--fs-xs)" }}>
          <span className="tnum" style={{ color: "var(--ink-2)" }}>
            {cpw === null ? "—" : <><b>{formatCents(cpw)}</b>/wear</>}
          </span>
          <span className="muted">·</span>
          <span className="muted tnum">{g.wear_count} {g.wear_count === 1 ? "wear" : "wears"}</span>
          <span className="muted">·</span>
          <span className="muted">{dormancyCopy(g)}</span>
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: "0.3rem", alignItems: "stretch" }}>
        <button className={`btn btn-sm wore-btn ${wore ? "logged" : "btn-primary"}`} title="Log a wear for today" aria-label="Log a wear"
          style={{ whiteSpace: "nowrap", minHeight: 32 }} onClick={onWear}>
          {wore ? (<><Check size={13} /> Logged</>) : "Wore it"}
        </button>
        <div className="row" style={{ gap: "0.2rem", justifyContent: "flex-end", flexWrap: "nowrap" }}>
          <button className="tile-act" title="Edit item" aria-label="Edit item" onClick={onEdit}><Pencil size={14} /></button>
          <button className="tile-act danger" title="Delete item" aria-label="Delete item" onClick={onDelete}><Trash2 size={14} /></button>
        </div>
      </div>
    </div>
  );
}

// Map a stored colour name to a CSS colour for the swatch, when it's a recognisable word.
const CSS_COLORS: Record<string, string> = {
  black: "#1c1c1c", white: "#f3f3f3", grey: "#9a9a9a", gray: "#9a9a9a",
  navy: "#1f2d54", blue: "#3b6fd6", green: "#4a8a5c", red: "#c0463f",
  pink: "#e08ab0", purple: "#8a4fbf", brown: "#7a5440", beige: "#d8c7a8",
  cream: "#efe7d4", tan: "#cdaa7d", orange: "#d98a3a", yellow: "#e3c64b",
};
function cssColor(name: string): string | null {
  const n = name.trim().toLowerCase();
  return CSS_COLORS[n] ?? null;
}

// ── Garment form (bottom sheet) ─────────────────────────────────────────────────
function GarmentForm({ editing, onClose, onSaved }: { editing: Garment | null; onClose: () => void; onSaved: () => void }) {
  const isEditing = editing !== null;
  const [item, setItem] = useState(editing?.item ?? "");
  const [brand, setBrand] = useState(editing?.brand ?? "");
  const [category, setCategory] = useState(editing?.category ?? "");
  const [color, setColor] = useState(editing?.color ?? "");
  const [price, setPrice] = useState(editing ? centsToInput(editing.price_cents) : "");
  const [purchasedOn, setPurchasedOn] = useState(editing?.purchased_on ?? "");
  const [photoRef, setPhotoRef] = useState(editing?.photo_ref ?? "");
  // "Fill details from photo" (parity #12) — the AI reads the garment and PRE-FILLS the form; the
  // owner still confirms by saving. Only fields the owner hasn't already typed are overwritten, so
  // tapping it never destroys their own input.
  const [guessBusy, setGuessBusy] = useState(false);
  // On-device background removal (iOS 17+). cutDone drives an honest label — we only claim the
  // background was removed when the native side actually reports changed=true.
  const [cutBusy, setCutBusy] = useState(false);
  const [cutDone, setCutDone] = useState(false);
  const [guessNote, setGuessNote] = useState("");
  async function fillFromPhoto() {
    if (guessBusy || !photoRef) return;
    setGuessBusy(true); setGuessNote("");
    try {
      const g = await identifyGarment(photoRef);
      if (g.item && !item.trim()) setItem(g.item);
      if (g.brand && !brand.trim()) setBrand(g.brand);
      if (g.category && !category.trim()) setCategory(g.category);
      if (g.color && !color.trim()) setColor(g.color);
      const extra = [g.fabric, g.fit].filter(Boolean).join(", ");
      const filled = [g.item && "name", g.category && "category", g.color && "colour", g.brand && "brand"].filter(Boolean).join(", ");
      setGuessNote(
        g.item || g.category || g.color
          ? `Filled ${filled || "what it could read"}${extra ? ` · looks like ${extra}` : ""}${g.confidence !== "high" ? " — worth a check" : ""}. Edit anything that's off.`
          : (g.note || "Couldn't read the garment clearly — fill it in yourself."),
      );
    } catch (e) {
      setGuessNote(e instanceof BrainError ? e.message : "Couldn't read the photo — try again.");
    } finally { setGuessBusy(false); }
  }
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Photo picking — the desktop dialog is a silent no-op on iOS, so on iOS trigger a hidden
  // <input type=file> (opens the native photo/camera sheet), then store a downscaled JPEG
  // data URL on-device in photo_ref. Never uploaded — the recap only ever sees aggregates.
  const photoInputRef = useRef<HTMLInputElement>(null);
  async function pickPhoto() {
    setErr(null);
    if (platform() === "ios") { photoInputRef.current?.click(); return; }
    try {
      const p = await openFileDialog({ multiple: false, filters: [{ name: "Image", extensions: ["png", "jpg", "jpeg", "gif", "webp", "heic"] }] });
      if (typeof p === "string") setPhotoRef(p);
    } catch { /* cancelled */ }
  }
  function onPhotoInputChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const src = reader.result;
      if (typeof src !== "string") { setErr("Could not read that photo — try another."); return; }
      const img = new Image();
      img.onload = () => {
        const MAX = 1024;
        const scale = Math.min(1, MAX / Math.max(img.width, img.height));
        const w = Math.max(1, Math.round(img.width * scale)), h = Math.max(1, Math.round(img.height * scale));
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext("2d");
        if (!ctx) { setPhotoRef(src); return; }
        ctx.drawImage(img, 0, 0, w, h);
        // Downscale first, then try the on-device cutout. The plain photo is set IMMEDIATELY so
        // the form never waits on Vision; the cutout swaps in when it lands (or never, silently).
        let shot: string;
        try { shot = canvas.toDataURL("image/jpeg", 0.82); } catch { shot = src; }
        setPhotoRef(shot);
        setCutBusy(true);
        void store.removeBackground(shot)
          .then((r) => { if (r.changed) { setPhotoRef(r.image); setCutDone(true); } })
          .finally(() => setCutBusy(false));
      };
      img.onerror = () => setPhotoRef(src);
      img.src = src;
    };
    reader.onerror = () => setErr("Could not read that photo — try another.");
    reader.readAsDataURL(file);
  }

  async function save() {
    if (!item.trim()) { setErr("Give the item a name."); return; }
    setSaving(true); setErr(null);
    try {
      const payload = {
        item: item.trim(),
        brand: brand.trim(),
        category: normalizeLower(category),
        color: normalizeLower(color),
        price_cents: priceToCents(price),
        purchased_on: purchasedOn.trim(),
        photo_ref: photoRef,
      };
      if (isEditing) await store.updateGarment(editing!.id, payload);
      else await store.createGarment(payload);
      onSaved();
    } catch (e) {
      setErr(`Could not save — try again. (${e instanceof Error ? e.message : String(e)})`);
      setSaving(false);
    }
  }

  return (
    <BottomSheet title={isEditing ? "Edit this piece" : "Add a piece"} onClose={onClose}>
      <Field label="Item"><input autoFocus placeholder="e.g. Black ankle boots" value={item} onChange={(e) => setItem(e.target.value)} /></Field>

      <div className="row" style={{ gap: "0.6rem", alignItems: "center", margin: "0.2rem 0 0.7rem" }}>
        {photoRef ? (
          <img src={photoRef} alt="" style={{ width: 56, height: 56, borderRadius: 10, objectFit: "cover", flexShrink: 0, border: "1px solid var(--line, rgba(0,0,0,0.12))" }} />
        ) : (
          <div className="swatch" style={{ width: 56, height: 56, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, background: "var(--elev-2)" }} aria-hidden><Shirt size={20} /></div>
        )}
        <button type="button" className="btn btn-sm" onClick={pickPhoto} style={{ whiteSpace: "nowrap" }}>{photoRef ? "Change photo" : "Add photo"}</button>
        {photoRef && <button type="button" className="btn btn-sm" onClick={() => setPhotoRef("")} style={{ whiteSpace: "nowrap" }}>Remove</button>}
        <input ref={photoInputRef} type="file" accept="image/*" onChange={onPhotoInputChange} style={{ display: "none" }} />
      </div>
      {photoRef && (
        <div style={{ margin: "-0.2rem 0 0.5rem" }}>
          <button type="button" className="btn btn-sm btn-primary" disabled={guessBusy} onClick={fillFromPhoto} style={{ whiteSpace: "nowrap" }}>
            <Sparkles size={13} /> {guessBusy ? "Reading the photo…" : "Fill details from photo"}
          </button>
          {guessNote && <div className="muted" style={{ fontSize: "var(--fs-xxs)", marginTop: "0.4rem", lineHeight: 1.5 }}>{guessNote}</div>}
        </div>
      )}
      {/* 🔴 HONEST COPY — this line used to read "Nothing is uploaded". That stopped being true the
          moment "Fill details from photo" shipped (2026-08-11): that button sends the photo for ONE
          billed call. Saying otherwise would be a false privacy claim in the app itself. */}
      <div className="muted photo-note" style={{ fontSize: "var(--fs-xxs)", margin: "-0.1rem 0 0.8rem", lineHeight: 1.5 }}>
        {cutBusy ? "Tidying the background on your device…" : cutDone
          ? "Background removed on your device — the photo never left it."
          : "Your photo is kept on this device."}{" "}
        If you tap “Fill details from photo”, it’s sent for that one reading and isn’t kept afterwards; a backup also carries it to your own storage.
      </div>

      <div className="row" style={{ gap: "0.6rem", alignItems: "stretch", flexWrap: "nowrap" }}>
        <Field label="Brand (optional)" style={{ flex: 1, minWidth: 0 }}><input placeholder="e.g. Clarks" value={brand} onChange={(e) => setBrand(e.target.value)} /></Field>
        <Field label="Price (optional)" style={{ flex: 1, minWidth: 0 }}><input inputMode="decimal" placeholder="129.99" value={price} onChange={(e) => setPrice(e.target.value)} /></Field>
      </div>

      <div style={{ fontSize: "var(--fs-xs)", fontWeight: 600, color: "var(--ink-2)", marginBottom: "0.5rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>Category</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", marginBottom: "0.55rem" }}>
        {DEFAULT_CATEGORIES.map((c) => (
          <button key={c} type="button" className={`chip${normalizeLower(category) === c ? " selected" : ""}`} onClick={() => setCategory(c)}>{titleCase(c)}</button>
        ))}
      </div>
      <input placeholder="Or type a category…" value={category} onChange={(e) => setCategory(e.target.value)} style={{ marginBottom: "0.9rem" }} />

      <div style={{ fontSize: "var(--fs-xs)", fontWeight: 600, color: "var(--ink-2)", marginBottom: "0.5rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>Colour</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", marginBottom: "0.55rem" }}>
        {DEFAULT_COLORS.map((c) => (
          <button key={c} type="button" className={`chip${normalizeLower(color) === c ? " selected" : ""}`} onClick={() => setColor(c)}>
            <span style={{ width: 10, height: 10, borderRadius: 3, background: cssColor(c) ?? "var(--ink-3)", display: "inline-block", border: "1px solid rgba(0,0,0,0.1)" }} />
            {titleCase(c)}
          </button>
        ))}
      </div>
      <input placeholder="Or type a colour…" value={color} onChange={(e) => setColor(e.target.value)} style={{ marginBottom: "0.9rem" }} />

      <Field label="Date bought (optional)"><input type="date" value={purchasedOn} onChange={(e) => setPurchasedOn(e.target.value)} /></Field>

      {err && <div className="notice notice-err" style={{ margin: "0.25rem 0 0.25rem" }}><span style={{ fontSize: "var(--fs-xs)" }}>{err}</span></div>}
      <div className="row" style={{ gap: "0.6rem", marginTop: "1.1rem", flexWrap: "nowrap" }}>
        <button className="btn btn-pill" style={{ flex: 1 }} onClick={onClose} disabled={saving}>Cancel</button>
        <button className="btn btn-primary btn-pill" style={{ flex: 2 }} onClick={save} disabled={!item.trim() || saving}>{saving ? "Saving…" : isEditing ? "Update" : "Add item"}</button>
      </div>
    </BottomSheet>
  );
}

function Field({ label, children, style }: { label: string; children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <div style={{ marginBottom: "0.8rem", ...style }}>
      <div style={{ fontSize: "var(--fs-xs)", fontWeight: 600, color: "var(--ink-2)", marginBottom: 5, textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</div>
      {children}
    </div>
  );
}

// Bottom sheet for forms — iOS-native feel; slides up from the bottom edge.
function BottomSheet({ title, children, onClose }: { title: string; children: React.ReactNode; onClose: () => void }) {
  return (
    <>
      <div className="sheet-scrim" onClick={onClose} />
      <div className="sheet sheet-bottom" role="dialog" aria-modal="true" aria-label={title}>
        <div className="sheet-grab" />
        <div className="row" style={{ justifyContent: "space-between", marginBottom: "1.1rem" }}>
          <div className="serif" style={{ fontWeight: 600, fontSize: "1.2rem", letterSpacing: "-0.01em" }}>{title}</div>
          <button className="tile-act" onClick={onClose} aria-label="Close"><X size={18} /></button>
        </div>
        {children}
      </div>
    </>
  );
}

// Centered modal — for the small destructive confirm.
function CenterModal({ title, children, onClose }: { title: string; children: React.ReactNode; onClose: () => void }) {
  return (
    <>
      <div className="sheet-scrim" onClick={onClose} />
      <div className="sheet sheet-center" role="dialog" aria-modal="true" aria-label={title}>
        <div className="row" style={{ justifyContent: "space-between", marginBottom: "0.9rem" }}>
          <div className="serif" style={{ fontWeight: 600, fontSize: "1.15rem" }}>{title}</div>
          <button className="tile-act" onClick={onClose} aria-label="Close"><X size={17} /></button>
        </div>
        {children}
      </div>
    </>
  );
}
